import { useState } from 'react'
import ChevDownIcon from '../../icons/ChevDownIcon'
import ChevUpIcon from '../../icons/ChevUpIcon'
import { SpinnerWithBackground } from '../Spinner'
import { Text } from '../Text'
import { getTimeAgo } from './helpers'
import type { DealTeamTriggerLog } from './types'

interface DealTeamTriggerHistorySectionProps {
	logs: DealTeamTriggerLog[]
	isLoading: boolean
	isError: boolean
	onExpandedChange?: (expanded: boolean) => void
	page?: number
	perPage?: number
	total?: number
	onPageChange?: (page: number) => void
}

const getSourceBadge = (source: string) => {
	const config: Record<string, string> = {
		manual_form: 'Form',
		manual_csv: 'CSV',
		webhook: 'Webhook',
		crm: 'CRM',
	}
	const label = config[source] || source
	return <span className='px-2.5 py-0.5 text-xs font-medium rounded-full bg-neutral-100 text-neutral-600'>{label}</span>
}

const getStatusBadge = (status: string) => {
	const config: Record<string, { bg: string; text: string; label: string }> = {
		pending: { bg: 'bg-neutral-100', text: 'text-neutral-600', label: 'Pending' },
		processing: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Processing' },
		completed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Completed' },
		failed: { bg: 'bg-red-100', text: 'text-red-700', label: 'Failed' },
	}
	const c = config[status] || config.pending
	return <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${c.bg} ${c.text}`}>{c.label}</span>
}

const formatDuration = (processingTimeMs: number | null) => {
	if (processingTimeMs === null || processingTimeMs === undefined) return '—'
	return `${(processingTimeMs / 1000).toFixed(1)}s`
}

const getTriggeredBy = (triggeredByName: string | null, source: string) => {
	if (triggeredByName) return triggeredByName
	if (source === 'webhook' || source === 'crm') return 'System'
	return '—'
}

export function DealTeamTriggerHistorySection({
	logs,
	isLoading,
	isError,
	onExpandedChange,
	page,
	perPage,
	total,
	onPageChange,
}: DealTeamTriggerHistorySectionProps) {
	const showPagination =
		typeof page === 'number' &&
		typeof perPage === 'number' &&
		typeof total === 'number' &&
		typeof onPageChange === 'function' &&
		total > perPage
	const totalPages = showPagination ? Math.max(1, Math.ceil((total ?? 0) / (perPage ?? 1))) : 1
	const rangeStart = showPagination ? ((page ?? 1) - 1) * (perPage ?? 0) + 1 : 0
	const rangeEnd = showPagination ? Math.min((page ?? 1) * (perPage ?? 0), total ?? 0) : 0
	const [isExpanded, setIsExpanded] = useState(false)
	const [expandedLogIds, setExpandedLogIds] = useState<Set<string>>(new Set())

	const toggleExpanded = () => {
		const next = !isExpanded
		setIsExpanded(next)
		onExpandedChange?.(next)
	}

	const toggleLogDetails = (logId: string) => {
		setExpandedLogIds((prev) => {
			const next = new Set(prev)
			if (next.has(logId)) {
				next.delete(logId)
			} else {
				next.add(logId)
			}
			return next
		})
	}

	return (
		<div className='border border-neutral-200 rounded-lg overflow-hidden bg-white shadow-sm'>
			<button
				type='button'
				onClick={toggleExpanded}
				className='w-full flex items-center justify-between px-6 py-4 bg-neutral-50 hover:bg-neutral-100 transition-colors'>
				<Text variant='small' weight='semibold' className='text-neutral-700'>
					Recent Trigger History
				</Text>
				{isExpanded ? (
					<ChevUpIcon className='w-4 h-4 text-neutral-500' />
				) : (
					<ChevDownIcon className='w-4 h-4 text-neutral-500' />
				)}
			</button>

			{isExpanded && (
				<div className='border-t border-neutral-200'>
					{isLoading ? (
						<div className='py-12'>
							<SpinnerWithBackground backgroundClass='opacity-100' />
						</div>
					) : isError ? (
						<div className='py-6 px-6'>
							<Text variant='small' className='text-red-500'>
								Failed to load trigger history
							</Text>
						</div>
					) : logs.length === 0 ? (
						<div className='py-12 flex items-center justify-center'>
							<Text variant='small' className='text-neutral-400'>
								No trigger history yet
							</Text>
						</div>
					) : (
						<>
							<div className='grid grid-cols-[140px_100px_1fr_120px_100px_1fr] gap-4 px-6 py-3 bg-neutral-50 border-b border-neutral-200'>
								<Text variant='xsmall' weight='semibold' className='text-neutral-500 uppercase tracking-wider'>
									Date
								</Text>
								<Text variant='xsmall' weight='semibold' className='text-neutral-500 uppercase tracking-wider'>
									Source
								</Text>
								<Text variant='xsmall' weight='semibold' className='text-neutral-500 uppercase tracking-wider'>
									Cycle Name
								</Text>
								<Text variant='xsmall' weight='semibold' className='text-neutral-500 uppercase tracking-wider'>
									Status
								</Text>
								<Text variant='xsmall' weight='semibold' className='text-neutral-500 uppercase tracking-wider'>
									Duration
								</Text>
								<Text variant='xsmall' weight='semibold' className='text-neutral-500 uppercase tracking-wider'>
									Triggered By
								</Text>
							</div>

							{logs.map((log) => (
								<div key={log.id} className='border-b border-neutral-100 last:border-b-0'>
									<div className='grid grid-cols-[140px_100px_1fr_120px_100px_1fr] gap-4 px-6 py-4 items-center hover:bg-neutral-50 transition-colors'>
										<Text variant='small' className='text-neutral-600'>
											{getTimeAgo(new Date(log.createdAt))}
										</Text>
										<div>{getSourceBadge(log.source)}</div>
										<Text variant='small' className='text-neutral-900 truncate'>
											{log.cycleName || '—'}
										</Text>
										<div>{getStatusBadge(log.status)}</div>
										<Text variant='small' className='text-neutral-600'>
											{formatDuration(log.processingTimeMs)}
										</Text>
										<div className='flex flex-col items-start gap-1 min-w-0'>
											<Text variant='small' className='text-neutral-600 w-full truncate'>
												{getTriggeredBy(log.triggeredByName, log.source)}
											</Text>
											{(log.errorMessage != null || log.errorMetadata != null) && (
												<button
													type='button'
													onClick={() => toggleLogDetails(log.id)}
													className='text-xs text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap'>
													{expandedLogIds.has(log.id) ? 'Hide Details' : 'View Details'}
												</button>
											)}
										</div>
									</div>
									{expandedLogIds.has(log.id) && (
										<div className='px-6 pb-4'>
											<div className='rounded-md border border-red-200 bg-red-50 px-4 py-3 flex flex-col gap-2'>
												<Text variant='xsmall' weight='semibold' className='text-red-700'>
													Trigger Error Details
												</Text>
												{log.errorMessage && (
													<Text variant='xsmall' className='text-red-700'>
														{log.errorMessage}
													</Text>
												)}
												{log.errorMetadata != null && (
													<pre className='text-[11px] leading-4 text-red-800 whitespace-pre-wrap break-words'>
														{JSON.stringify(log.errorMetadata, null, 2) as string}
													</pre>
												)}
											</div>
										</div>
									)}
								</div>
							))}

							{showPagination && (
								<div className='flex items-center justify-between px-6 py-3 border-t border-neutral-200 bg-neutral-50'>
									<Text variant='xsmall' className='text-neutral-500'>
										Showing {rangeStart}–{rangeEnd} of {total}
									</Text>
									<div className='flex items-center gap-2'>
										<button
											type='button'
											onClick={() => onPageChange?.(Math.max(1, (page ?? 1) - 1))}
											disabled={(page ?? 1) <= 1 || isLoading}
											className='px-3 py-1 text-xs font-medium rounded border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'>
											Previous
										</button>
										<Text variant='xsmall' className='text-neutral-600'>
											Page {page} of {totalPages}
										</Text>
										<button
											type='button'
											onClick={() => onPageChange?.(Math.min(totalPages, (page ?? 1) + 1))}
											disabled={(page ?? 1) >= totalPages || isLoading}
											className='px-3 py-1 text-xs font-medium rounded border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'>
											Next
										</button>
									</div>
								</div>
							)}
						</>
					)}
				</div>
			)}
		</div>
	)
}
